import {
  AuthDivider,
  AuthLayout,
  Button,
  ErrorState,
  InlineMessage,
  RegisterForm,
  SignInForm,
  SSOButton,
  Stack,
  Text,
  VerifyCodeForm,
} from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fetchPartnerMe, fetchReferral, fetchReferrals, submitReferral, submitRegistration, updatePartnerMe } from './api';
import { ApplyPage, DeactivatedPage, DeclinedPage, PendingPage } from './ApplicationPages';
import { partnerAuth } from './auth';
import { createPartnerFlow, maskEmail, type PartnerFlow, type ProfileValues, type Screen, SIGN_IN_PATH, SIGN_UP_PATH, signedOutScreen } from './flow';
import { LeadPage, LeadsFailedPage, MyLeadsPage, NoSuchLeadPage } from './LeadPages';
import { leadPath, MY_LEADS_PATH } from './leads';
import { PageLink } from './PageLink';
import { ProfilePage } from './ProfilePage';
import { ReferPage } from './ReferPage';
import type { ReferralValues } from './referral';
import type { RegistrationValues } from './registration';

/** What each screen can ask the page to do. */
export interface ScreenActions {
  signIn(input: { email: string; password: string }): void;
  signUp(input: { firstName: string; lastName: string; email: string; password: string }): void;
  verify(code: string): void;
  resend(): void;
  google(): void;
  signOut(): void;
  /** Submits the Apply page. */
  apply(values: RegistrationValues): void;
  retry(): void;
  /** Goes to /sign-in or /sign-up without a page load. */
  go(path: string): void;
  /** A verified partner: goes to My Leads or a lead's page without a page load. */
  open(path: string): void;
  /** A verified partner: reads the page that failed again (Try Again). */
  reopen(): void;
  /** A verified partner: submits the Refer a Company form (MOTHER-88). */
  refer(values: ReferralValues): void;
  /** A verified partner: saves My Profile's phone and website (MOTHER-182). */
  saveProfile(values: ProfileValues): void;
  /** A verified partner: puts My Profile back to the loaded values, sending nothing. */
  cancelProfile(): void;
}

type SignedIn = Extract<Screen, { kind: 'signed-in' }>;

/**
 * The signed-in page, chosen by the partner record /api/partner/me returned, so a reload
 * shows the same page. This is the one place a status picks its page.
 */
function SignedInPage({ screen, busy, actions }: { screen: SignedIn; busy: boolean; actions: ScreenActions }): JSX.Element {
  const { me, error } = screen;
  const closed = { me, busy, error, onSignOut: actions.signOut };
  switch (me.partner?.status) {
    case undefined:
      return <ApplyPage me={me} application={screen.application} busy={busy} error={error} onApply={actions.apply} onSignOut={actions.signOut} />;
    case 'pending':
      return <PendingPage {...closed} />;
    case 'declined':
      return <DeclinedPage {...closed} />;
    case 'deactivated':
      return <DeactivatedPage {...closed} />;
    case 'verified':
      return <VerifiedPage screen={screen} busy={busy} actions={actions} />;
    default:
      return <HolderPage {...closed} />;
  }
}

/**
 * A verified partner (MOTHER-87): My Leads, a lead's page, the Refer a Company form
 * (MOTHER-88), My Profile (MOTHER-182), or loading while it is read.
 */
function VerifiedPage({ screen, busy, actions }: { screen: SignedIn; busy: boolean; actions: ScreenActions }): JSX.Element {
  const { me, leads, error } = screen;
  const page = { busy, error, onSignOut: actions.signOut, onOpen: actions.open };
  switch (leads?.kind) {
    case undefined:
      return (
        <AuthLayout title="Olytic Partners" description="Loading your leads…">
          {null}
        </AuthLayout>
      );
    case 'list':
      return <MyLeadsPage me={me} referrals={leads.referrals} {...page} />;
    case 'lead':
      return <LeadPage referral={leads.referral} notice={leads.notice} {...page} />;
    case 'no-such-lead':
      return <NoSuchLeadPage {...page} />;
    case 'failed':
      return <LeadsFailedPage onRetry={actions.reopen} {...page} />;
    case 'refer':
      return <ReferPage form={leads.form} onRefer={actions.refer} {...page} />;
    case 'profile':
      return <ProfilePage me={me} view={leads} onSave={actions.saveProfile} onCancel={actions.cancelProfile} {...page} />;
  }
}

/** Item 1's holder screen, now only for a status this page does not know. */
function HolderPage({ me, busy, error, onSignOut }: { me: SignedIn['me']; busy: boolean; error: string | undefined; onSignOut: () => void }): JSX.Element {
  return (
    <AuthLayout title="You're Signed In" description={`Signed in as ${me.email}.`}>
      <Stack gap="md">
        {/* Only after a failed Sign Out: the session is still live, and the button retries. */}
        {error ? (
          <InlineMessage tone="error" role="alert">
            {error}
          </InlineMessage>
        ) : null}
        <Button variant="neutral" onClick={onSignOut} loading={busy}>
          Sign Out
        </Button>
      </Stack>
    </AuthLayout>
  );
}

/**
 * The partner page for one screen: Foundation's AuthLayout, auth forms and form fields,
 * and nothing else. Props only, so tests render each screen with fake data.
 */
export function PartnerScreen({ screen, busy, actions }: { screen: Screen; busy: boolean; actions: ScreenActions }): JSX.Element {
  switch (screen.kind) {
    case 'loading':
      return (
        <AuthLayout title="Olytic Partners" description="Checking your session…">
          {null}
        </AuthLayout>
      );
    case 'sign-in':
      return (
        <AuthLayout title="Olytic Partners" description="Sign in to refer companies to Olytic and follow your leads.">
          <Stack gap="md">
            {screen.notice ? <InlineMessage tone="success">{screen.notice}</InlineMessage> : null}
            <SSOButton provider="google" onClick={actions.google} disabled={busy} />
            <AuthDivider />
            <SignInForm onSubmit={actions.signIn} busy={busy} {...(screen.error ? { error: screen.error } : {})} />
            <Text tone="muted" size="sm">
              New to Olytic Partners? <PageLink path={SIGN_UP_PATH} go={actions.go}>Create an account</PageLink>
            </Text>
          </Stack>
        </AuthLayout>
      );
    case 'sign-up':
      return (
        <AuthLayout title="Create an Account" description="Sign up with Google, or with your email and a password.">
          <Stack gap="md">
            {/* No onSignIn and no terms: RegisterForm draws its Sign in as an unstyled button
                (Foundation 0.80.0), so the page links to /sign-in itself, as sign-in links here. */}
            <RegisterForm
              providers={['google']}
              onSSO={actions.google}
              onSubmit={actions.signUp}
              busy={busy}
              {...(screen.error ? { error: screen.error } : {})}
            />
            <Text tone="muted" size="sm">
              Already have an account? <PageLink path={SIGN_IN_PATH} go={actions.go}>Sign in</PageLink>
            </Text>
          </Stack>
        </AuthLayout>
      );
    case 'verify':
      return (
        <AuthLayout title="Check your email" description="Enter the 6-digit code to confirm your address.">
          <Stack gap="md">
            {screen.notice ? <InlineMessage tone="success">{screen.notice}</InlineMessage> : null}
            <VerifyCodeForm
              length={6}
              destination={maskEmail(screen.email)}
              onSubmit={actions.verify}
              onResend={actions.resend}
              busy={busy}
              {...(screen.error ? { error: screen.error } : {})}
            />
            <Text tone="muted" size="sm">
              <PageLink path={SIGN_IN_PATH} go={actions.go}>Back to sign in</PageLink>
            </Text>
          </Stack>
        </AuthLayout>
      );
    case 'signed-in':
      return <SignedInPage screen={screen} busy={busy} actions={actions} />;
    case 'error':
      return (
        <AuthLayout title="Olytic Partners">
          <ErrorState title="Something went wrong" description={screen.message} onRetry={actions.retry} />
        </AuthLayout>
      );
  }
}

/**
 * The partner portal on partners.olytic.com (MOTHER-69, MOTHER-86): sign in with Google
 * or an email and password, sign up with the address confirmed by an emailed code, then
 * apply, and a page for each state of the application; a verified partner sees My Leads
 * at / and each lead at /leads/<id> (MOTHER-87), and refers a company at /refer
 * (MOTHER-88) and changes their phone and website at /profile (MOTHER-182). No router: a
 * pathname switch with
 * history.pushState; any other path signed out is the sign-in form.
 */
export function PartnersPage({ flow: given }: { flow?: PartnerFlow } = {}): JSX.Element {
  const flow = useMemo(
    () =>
      given ??
      createPartnerFlow({
        auth: partnerAuth(),
        fetchMe: () => fetchPartnerMe(() => partnerAuth().getToken()),
        register: (body) => submitRegistration(() => partnerAuth().getToken(), body),
        fetchReferrals: () => fetchReferrals(() => partnerAuth().getToken()),
        fetchReferral: (id) => fetchReferral(() => partnerAuth().getToken(), id),
        refer: (body) => submitReferral(() => partnerAuth().getToken(), body),
        updateProfile: (change) => updatePartnerMe(() => partnerAuth().getToken(), change),
      }),
    [given],
  );
  const [screen, setScreen] = useState<Screen>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const screenRef = useRef(screen);
  screenRef.current = screen;

  /** The latest action started: only its answer may move the page. */
  const latest = useRef(0);

  /**
   * Runs one action, showing the form busy until its next screen arrives. An action started
   * after it (Sign Out while an application is still being sent) supersedes it, and its
   * late answer is dropped: a registration that resolves after Sign Out must not put the
   * signed-out person back on a signed-in page. `commit` holds the action's side effects
   * on the address bar, and runs only when its answer is still the latest: a superseded
   * My Leads read must not rewrite the address of the lead opened after it.
   */
  const run = useCallback((next: () => Promise<Screen>, commit?: (resolved: Screen) => void) => {
    const ticket = ++latest.current;
    setBusy(true);
    void next().then((resolved) => {
      if (ticket !== latest.current) return;
      commit?.(resolved);
      setScreen(resolved);
      setBusy(false);
    });
  }, []);

  const load = useCallback(() => {
    setScreen({ kind: 'loading' });
    run(() => flow.load(window.location.pathname));
  }, [flow, run]);

  useEffect(load, [load]);

  /** Reads a verified partner's page for `path`; once it is My Leads, the address is /. */
  const openLeads = useCallback(
    (path: string) => {
      const current = screenRef.current;
      if (current.kind !== 'signed-in') return;
      run(
        () => flow.openLeads(current.me, path),
        (next) => {
          if (next.kind === 'signed-in' && next.leads?.kind === 'list' && window.location.pathname !== MY_LEADS_PATH) {
            window.history.replaceState(null, '', MY_LEADS_PATH);
          }
          if (next.kind === 'sign-in') window.history.replaceState(null, '', '/');
        },
      );
    },
    [flow, run],
  );

  // A verified partner whose page is not read yet (just signed in, or reloaded): read it.
  const needsLeads = screen.kind === 'signed-in' && screen.me.partner?.status === 'verified' && screen.leads === undefined && !busy;
  useEffect(() => {
    if (needsLeads) openLeads(window.location.pathname);
  }, [needsLeads, openLeads]);

  // Back and forward: between /sign-in and /sign-up while signed out, and between My Leads and a lead's page.
  useEffect(() => {
    const onPop = () => {
      const current = screenRef.current;
      if (current.kind === 'sign-in' || current.kind === 'sign-up') setScreen(signedOutScreen(window.location.pathname));
      else if (current.kind === 'signed-in' && current.leads !== undefined) openLeads(window.location.pathname);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [openLeads]);

  const go = useCallback((path: string) => {
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
    setScreen(signedOutScreen(path));
  }, []);

  /** A referral is being sent: a second submit (a double click) sends nothing more. */
  const referring = useRef(false);
  /** My Profile is being saved: likewise, one PATCH for a double click. */
  const saving = useRef(false);

  const verifyEmail = screen.kind === 'verify' ? screen.email : '';
  const actions: ScreenActions = {
    signIn: (input) => run(() => flow.signIn(input)),
    signUp: (input) => run(() => flow.signUp(input)),
    verify: (code) => run(() => flow.verify(verifyEmail, code)),
    resend: () => run(() => flow.resend(verifyEmail)),
    google: () => run(() => flow.google(screenRef.current)),
    signOut: () => {
      const current = screenRef.current;
      if (current.kind !== 'signed-in') return;
      run(
        async () => {
          const next = await flow.signOut(current.me);
          // A failed Sign Out keeps the page it was on: the Apply page's last submit, or the lead shown.
          if (next.kind === 'signed-in' && current.application) return { ...next, application: current.application };
          if (next.kind === 'signed-in' && current.leads) return { ...next, leads: current.leads };
          return next;
        },
        // Back to / only once signed out; a failed Sign Out stays where it is.
        (next) => {
          if (next.kind === 'sign-in') window.history.replaceState(null, '', '/');
        },
      );
    },
    apply: (values) => {
      const current = screenRef.current;
      if (current.kind !== 'signed-in') return;
      run(() => flow.apply(current.me, values));
    },
    refer: (values) => {
      const current = screenRef.current;
      if (current.kind !== 'signed-in' || referring.current) return;
      referring.current = true;
      run(
        async () => {
          try {
            return await flow.refer(current.me, values);
          } finally {
            referring.current = false;
          }
        },
        (next) => {
          // The new lead's page replaces the form in history, so going back is My Leads, not the form again.
          if (next.kind === 'signed-in' && next.leads?.kind === 'lead') window.history.replaceState(null, '', leadPath(next.leads.referral.id));
          if (next.kind === 'sign-in') window.history.replaceState(null, '', '/');
        },
      );
    },
    saveProfile: (values) => {
      const current = screenRef.current;
      if (current.kind !== 'signed-in' || saving.current) return;
      saving.current = true;
      run(
        async () => {
          try {
            return await flow.saveProfile(current.me, values);
          } finally {
            saving.current = false;
          }
        },
        (next) => {
          if (next.kind === 'sign-in') window.history.replaceState(null, '', '/');
        },
      );
    },
    cancelProfile: () => {
      const current = screenRef.current;
      if (current.kind !== 'signed-in' || current.leads?.kind !== 'profile' || saving.current) return;
      setScreen({ ...current, leads: { kind: 'profile' } });
    },
    retry: load,
    go,
    open: (path) => {
      if (window.location.pathname !== path) window.history.pushState(null, '', path);
      openLeads(path);
    },
    reopen: () => {
      const current = screenRef.current;
      openLeads(current.kind === 'signed-in' && current.leads?.kind === 'failed' ? current.leads.path : window.location.pathname);
    },
  };

  return <PartnerScreen screen={screen} busy={busy} actions={actions} />;
}
